import { DateRangePicker } from 'rsuite';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ReportType } from 'src/features/reports/types';
import { NO_DATE_LIMIT_AGGREGATION_INTERVALS } from 'src/features/reports/config/config';

type Props = {
  value: [string, string];
  onChange: (val: [string, string]) => void;
  reportType?: ReportType;
  isMobile?: boolean;
  disabled?: boolean;
};

export const TimeFrameRangePicker = ({
  value,
  onChange,
  reportType,
  isMobile,
  disabled,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { watch } = useFormContext();
  const aggregationInterval = watch('data.aggregationInterval');
  const theme = useTheme();
  let diffLimitInDays: number | null = 183;
  if (aggregationInterval && NO_DATE_LIMIT_AGGREGATION_INTERVALS.includes(aggregationInterval)) {
    diffLimitInDays = null;
  }
  if (reportType && reportType === ReportType.PDF) diffLimitInDays = null;

  useEffect(() => {
    const cssVariables = {
      '--rs-btn-primary-bg': theme.palette.primary.main,
      '--rs-btn-primary-hover-bg': theme.palette.primary.main,
      '--rs-btn-primary-active-bg': theme.palette.primary.main,
      '--rs-bg-active': theme.palette.primary.main,
      '--rs-calendar-range-bg': theme.palette.primary.lighter,
      '--rs-state-hover-bg': theme.palette.primary.lighter,
      '--rs-calendar-cell-selected-hover-bg': theme.palette.primary.main,
      '--rs-listbox-option-hover-text': theme.palette.primary.main,
      '--rs-listbox-option-hover-bg': theme.palette.primary.lighter,
      '--rs-input-focus-border': theme.palette.primary.main,
      '--rs-color-focus-ring': theme.palette.primary.lighter,
    };
    Object.entries(cssVariables).forEach(([variable, color]) => {
      document.documentElement.style.setProperty(variable, color);
    });
  }, [theme]);
  return (
    <DateRangePicker
      showOneCalendar={isMobile}
      disabled={disabled}
      className="custom-date-range-picker"
      cleanable={false}
      placement={'bottomEnd'}
      ranges={[]}
      style={{ width: '100%' }}
      value={[dayjs(value[0], 'YYYY-MM-DD').toDate(), dayjs(value[1], 'YYYY-MM-DD').toDate()]}
      onChange={(dates) => {
        if (dates && dates.length === 2) {
          const [startDate, endDate] = dates;
          const diffInDays = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

          if (diffLimitInDays !== null && diffInDays > diffLimitInDays) {
            enqueueSnackbar(`Please select a range within 6 months.`, {
              variant: 'error',
            });
            return;
          }
          onChange(dates.map((d) => dayjs(d).format('YYYY-MM-DD')) as [string, string]);
        } else {
          onChange(['', '']);
        }
      }}
    />
  );
};
